import { useLocation } from 'react-router-dom';
import {
  Building2,
  LayoutDashboard,
  BarChart3,
  Send,
  Users,
  Settings,
  Radio,
  FolderKanban,
  User as UserIcon,
  Lock,
  CreditCard,
  Layers,
  Server,
  Activity,
  Table2,
  Database,
  Columns,
  Code,
  Mail,
  Webhook,
  ShieldAlert,
  Paperclip,
  LucideIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: LucideIcon;
  badge?: number | string;
  isActive: boolean;
  end?: boolean;
}

export interface NavigationState {
  orgId: string;
  projId: string;
  tableId: string;
  isHomeMode: boolean;
  isAdminMode: boolean;
  isAccountMode: boolean;
  isProjectScope: boolean;
  isTableScope: boolean;
  isOrgMode: boolean;

  orgNavItems: NavItem[];
  projectNavItems: NavItem[];
  tableNavItems: NavItem[];
  accountNavItems: NavItem[];
  adminNavItems: NavItem[];

  activeOrgItem: NavItem;
  activeProjectItem: NavItem;
  activeTableItem: NavItem;
  activeAccountItem: NavItem;
  activeAdminItem: NavItem;
  currentSubpageItem: NavItem;
  currentSubpageItems: NavItem[];
}

export const useNavigation = (): NavigationState => {
  const location = useLocation();
  const {
    currentOrg,
    currentProject,
    currentTable,
    tables,
    projects,
    organizations,
    user,
    role,
  } = useAuth();
  const canManageOrg = Boolean(user?.is_superadmin || role === 'owner' || role === 'admin');
  const pathname = location.pathname;

  const orgMatch = pathname.match(/\/organization\/([^/]+)/);
  const orgId = orgMatch ? orgMatch[1] : (currentOrg?.id || '');

  const projMatch = pathname.match(/\/projects\/([^/]+)/);
  const projId = projMatch ? projMatch[1] : (currentProject?.id || (projects.length > 0 ? projects[0].id : ''));

  const tableMatch = pathname.match(/\/tables\/([^/]+)/);
  const tableId = tableMatch ? tableMatch[1] : (currentTable?.id || '');

  const isHomeMode = pathname === '/' || pathname === '';
  const isAdminMode = pathname.startsWith('/admin');
  const isAccountMode = pathname.startsWith('/account');
  const isTableScope = pathname.includes('/tables');
  const isProjectScope =
    !isAdminMode &&
    !isAccountMode &&
    Boolean(projId) &&
    pathname.includes('/projects/');
  const isOrgMode = !isHomeMode && !isAdminMode && !isAccountMode && !isProjectScope;

  // 1. Organization Scope Items
  const isOrgOverview =
    pathname === `/organization/${orgId}` ||
    pathname === `/organization/${orgId}/` ||
    pathname === `/organization/${orgId}/overview`;
  const isOrgProjects =
    pathname === `/organization/${orgId}/projects` ||
    pathname === `/organization/${orgId}/projects/` ||
    isProjectScope ||
    isTableScope;
  const isOrgMembers =
    pathname.startsWith(`/organization/${orgId}/members`) ||
    pathname.startsWith(`/organization/${orgId}/team`);
  const isOrgSettings = pathname.startsWith(`/organization/${orgId}/settings`);

  const orgNavItems: NavItem[] = [
    {
      id: 'overview',
      label: 'Overview',
      to: `/organization/${orgId}`,
      icon: LayoutDashboard,
      isActive: isOrgOverview,
      end: true,
    },
    {
      id: 'projects',
      label: 'Projects',
      to: `/organization/${orgId}/projects`,
      icon: FolderKanban,
      badge: projects.length,
      isActive: isOrgProjects,
    },
    ...(canManageOrg
      ? [
          {
            id: 'members',
            label: 'Members',
            to: `/organization/${orgId}/members`,
            icon: Users,
            isActive: isOrgMembers,
          },
          {
            id: 'settings',
            label: 'Settings',
            to: `/organization/${orgId}/settings`,
            icon: Settings,
            isActive: isOrgSettings,
          },
        ]
      : []),
  ];

  // 2. Project Scope Items
  const isProjDashboard =
    !isTableScope &&
    (pathname === `/organization/${orgId}/projects/${projId}` ||
      pathname === `/organization/${orgId}/projects/${projId}/` ||
      pathname === `/organization/${orgId}/projects/${projId}/dashboard`);
  const isProjDatastores =
    !isTableScope &&
    (pathname.includes('/datastores') ||
      pathname === `/organization/${orgId}/projects/${projId}/tables` ||
      pathname === `/organization/${orgId}/projects/${projId}/tables/`);
  const isProjAnalytics = !isTableScope && pathname.includes('/analytics');
  const isProjDeliveryLogs = !isTableScope && pathname.includes('/delivery-logs');
  const isProjProviders =
    !isTableScope &&
    (pathname.includes('/providers') || pathname.includes('/rules'));
  const isProjSettings =
    !isTableScope &&
    pathname.includes('/projects/') && pathname.endsWith('/settings');

  const projectNavItems: NavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      to: `/organization/${orgId}/projects/${projId}`,
      icon: LayoutDashboard,
      isActive: isProjDashboard,
      end: true,
    },
    {
      id: 'datastores',
      label: 'Datastores',
      to: `/organization/${orgId}/projects/${projId}/datastores`,
      icon: Database,
      badge: tables.length,
      isActive: isProjDatastores,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      to: `/organization/${orgId}/projects/${projId}/analytics`,
      icon: BarChart3,
      isActive: isProjAnalytics,
    },
    {
      id: 'delivery-logs',
      label: 'Delivery logs',
      to: `/organization/${orgId}/projects/${projId}/delivery-logs`,
      icon: Send,
      isActive: isProjDeliveryLogs,
    },
    {
      id: 'providers',
      label: 'Notification providers',
      to: `/organization/${orgId}/projects/${projId}/providers`,
      icon: Radio,
      isActive: isProjProviders,
    },
    {
      id: 'settings',
      label: 'Settings',
      to: `/organization/${orgId}/projects/${projId}/settings`,
      icon: Settings,
      isActive: isProjSettings,
    },
  ];

  // 3. Datastore (Table) Scope Items
  const baseTableRoute =
    orgId && projId
      ? `/organization/${orgId}/projects/${projId}/tables/${tableId}`
      : `/tables/${tableId}`;

  const isTableData =
    isTableScope &&
    (pathname === baseTableRoute ||
      pathname === `${baseTableRoute}/` ||
      pathname === `${baseTableRoute}/data` ||
      (!pathname.endsWith('/files') &&
        !pathname.endsWith('/schema') &&
        !pathname.endsWith('/endpoints') &&
        !pathname.endsWith('/rules') &&
        !pathname.endsWith('/notifications') &&
        !pathname.endsWith('/webhooks') &&
        !pathname.endsWith('/spam') &&
        !pathname.endsWith('/analytics') &&
        !pathname.endsWith('/settings')));
  const isTableFiles = isTableScope && pathname.endsWith('/files');
  const isTableSchema = isTableScope && pathname.endsWith('/schema');
  const isTableEndpoints = isTableScope && pathname.endsWith('/endpoints');
  const isTableRules =
    isTableScope &&
    (pathname.endsWith('/rules') || pathname.endsWith('/notifications'));
  const isTableWebhooks = isTableScope && pathname.endsWith('/webhooks');
  const isTableSpam = isTableScope && pathname.endsWith('/spam');
  const isTableAnalytics = isTableScope && pathname.endsWith('/analytics');
  const isTableSettings = isTableScope && pathname.endsWith('/settings');

  const tableNavItems: NavItem[] = [
    {
      id: 'data',
      label: 'Browse data',
      to: baseTableRoute,
      icon: Table2,
      isActive: isTableData,
      end: true,
    },
    {
      id: 'files',
      label: 'Files',
      to: `${baseTableRoute}/files`,
      icon: Paperclip,
      isActive: isTableFiles,
    },
    {
      id: 'schema',
      label: 'Schema',
      to: `${baseTableRoute}/schema`,
      icon: Columns,
      isActive: isTableSchema,
    },
    {
      id: 'endpoints',
      label: 'Endpoints',
      to: `${baseTableRoute}/endpoints`,
      icon: Code,
      isActive: isTableEndpoints,
    },
    {
      id: 'rules',
      label: 'Notifications',
      to: `${baseTableRoute}/rules`,
      icon: Mail,
      isActive: isTableRules,
    },
    {
      id: 'webhooks',
      label: 'Webhooks',
      to: `${baseTableRoute}/webhooks`,
      icon: Webhook,
      isActive: isTableWebhooks,
    },
    {
      id: 'spam',
      label: 'Spam protection',
      to: `${baseTableRoute}/spam`,
      icon: ShieldAlert,
      isActive: isTableSpam,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      to: `${baseTableRoute}/analytics`,
      icon: BarChart3,
      isActive: isTableAnalytics,
    },
    {
      id: 'settings',
      label: 'Settings',
      to: `${baseTableRoute}/settings`,
      icon: Settings,
      isActive: isTableSettings,
    },
  ];

  // 3. Account Scope Items
  const isAccountGeneral =
    pathname === '/account' || pathname === '/account/general';
  const isAccountSecurity = pathname.startsWith('/account/security');
  const isAccountBilling =
    pathname.startsWith('/account/billing') ||
    pathname.startsWith('/account/subscriptions');
  const isAccountOrgs = pathname.startsWith('/account/organizations');

  const accountNavItems: NavItem[] = [
    {
      id: 'general',
      label: 'Profile & General',
      to: '/account/general',
      icon: UserIcon,
      isActive: isAccountGeneral,
    },
    {
      id: 'security',
      label: 'Security & Credentials',
      to: '/account/security',
      icon: Lock,
      isActive: isAccountSecurity,
    },
    {
      id: 'billing',
      label: 'Billing & Plans',
      to: '/account/billing',
      icon: CreditCard,
      isActive: isAccountBilling,
    },
    {
      id: 'organizations',
      label: 'My Organizations',
      to: '/account/organizations',
      icon: Building2,
      badge: organizations.length,
      isActive: isAccountOrgs,
    },
  ];

  // 4. Admin Scope Items
  const isAdminOverview =
    pathname === '/admin' || pathname === '/admin/overview';
  const isAdminTenants = pathname.startsWith('/admin/tenants');
  const isAdminOrgs = pathname.startsWith('/admin/organizations');
  const isAdminPlans = pathname.startsWith('/admin/plans');
  const isAdminQueues = pathname.startsWith('/admin/queues');

  const adminNavItems: NavItem[] = [
    {
      id: 'overview',
      label: 'Overview & Telemetry',
      to: '/admin/overview',
      icon: Activity,
      isActive: isAdminOverview,
    },
    {
      id: 'tenants',
      label: 'Users',
      to: '/admin/tenants',
      icon: Users,
      isActive: isAdminTenants,
    },
    {
      id: 'organizations',
      label: 'Organizations & Quotas',
      to: '/admin/organizations',
      icon: Building2,
      isActive: isAdminOrgs,
    },
    {
      id: 'plans',
      label: 'Subscription Plans',
      to: '/admin/plans',
      icon: Layers,
      isActive: isAdminPlans,
    },
    {
      id: 'queues',
      label: 'Queue & Workers',
      to: '/admin/queues',
      icon: Server,
      isActive: isAdminQueues,
    },
  ];

  const activeOrgItem =
    orgNavItems.find((item) => item.isActive) || orgNavItems[0];
  const activeProjectItem =
    projectNavItems.find((item) => item.isActive) || projectNavItems[0];
  const activeTableItem =
    tableNavItems.find((item) => item.isActive) || tableNavItems[0];
  const activeAccountItem =
    accountNavItems.find((item) => item.isActive) || accountNavItems[0];
  const activeAdminItem =
    adminNavItems.find((item) => item.isActive) || adminNavItems[0];

  let currentSubpageItem = activeOrgItem;
  let currentSubpageItems = orgNavItems;

  if (isAdminMode) {
    currentSubpageItem = activeAdminItem;
    currentSubpageItems = adminNavItems;
  } else if (isAccountMode) {
    currentSubpageItem = activeAccountItem;
    currentSubpageItems = accountNavItems;
  } else if (isTableScope) {
    currentSubpageItem = activeTableItem;
    currentSubpageItems = tableNavItems;
  } else if (isProjectScope) {
    currentSubpageItem = activeProjectItem;
    currentSubpageItems = projectNavItems;
  }

  return {
    orgId,
    projId,
    tableId,
    isHomeMode,
    isAdminMode,
    isAccountMode,
    isProjectScope,
    isTableScope,
    isOrgMode,

    orgNavItems,
    projectNavItems,
    tableNavItems,
    accountNavItems,
    adminNavItems,

    activeOrgItem,
    activeProjectItem,
    activeTableItem,
    activeAccountItem,
    activeAdminItem,
    currentSubpageItem,
    currentSubpageItems,
  };
};
